import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Star,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { trackCTAClick } from "@/lib/analytics";
import { useLanguage } from "@/contexts/LanguageContext";
import { pickByLanguage } from "@/lib/language";
import { Eyebrow } from "@/components/landing/_primitives";
import heroModel from "@/assets/model.png";

interface HeroSectionProps {
  badgeText?: string;
  title?: string;
  subtitle?: string;
  primaryCtaLabel?: string;
}

export function HeroSection({
  badgeText,
  title,
  subtitle,
  primaryCtaLabel,
}: HeroSectionProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();

  const signupParams = new URLSearchParams(location.search);
  signupParams.set("mode", "signup");
  const signupPath = `/auth?${signupParams.toString()}`;

  const resolvedBadge =
    badgeText ??
    pickByLanguage(language, {
      pt: "A casa digital de creators, consultores e marcas pessoais",
      en: "The digital home for creators, consultants & personal brands",
    });

  const defaultTitleParts = pickByLanguage(language, {
    pt: { lead: "Sua identidade,", highlight: "amplificada." },
    en: { lead: "Your identity,", highlight: "amplified." },
  });

  const resolvedTitleNode = title ? (
    <>{title}</>
  ) : (
    <>
      {defaultTitleParts.lead}
      <br />
      <span className="text-gradient-creator">{defaultTitleParts.highlight}</span>
    </>
  );

  const resolvedSubtitle =
    subtitle ??
    pickByLanguage(language, {
      pt: "Construa o hub digital que reflete quem você é — uma página editorial, com loja, agenda e um agente de IA que conversa com sua audiência no seu tom de voz.",
      en: "Build the digital hub that reflects who you are — an editorial page with storefront, booking, and an AI agent that speaks to your audience in your voice.",
    });

  const resolvedPrimaryCta =
    primaryCtaLabel ??
    pickByLanguage(language, {
      pt: "Reivindicar meu link",
      en: "Claim my link",
    });

  const shouldReduceMotion = useReducedMotion();
  const rm = shouldReduceMotion;

  return (
    <section className="relative overflow-hidden min-h-svh flex flex-col justify-center pt-28 pb-14 sm:pt-32 sm:pb-20 lg:pb-24">
      {/* ── Background image placeholder — troque por <img> quando pronto ── */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#fff9f2_0%,#fdf2e6_42%,#fff7ef_100%)]">
        {/* Substitua o conteúdo desta div por: <img src="..." className="w-full h-full object-cover" /> */}
      </div>

      {/* Overlay suave para legibilidade do texto */}
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,249,242,0.92)_0%,rgba(255,249,242,0.92)_55%,rgba(255,249,242,0.4)_100%)] pointer-events-none" />

      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-[-10%] top-[8%] h-48 w-48 rounded-full bg-[#ffd9b8]/60 blur-3xl" />
        <div className="absolute right-[-8%] top-[12%] h-64 w-64 rounded-full bg-[#ffd0b0]/60 blur-3xl" />
        <div className="absolute bottom-0 left-1/2 h-48 w-[70%] -translate-x-1/2 rounded-full bg-[#fff1e1] blur-3xl" />
      </div>

      <div className="container relative">
        <div className="grid gap-8 lg:grid-cols-2 lg:gap-0">
          <div className="max-w-[37rem] lg:self-center">
            <motion.div
              initial={rm ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: rm ? 0 : 0.45, ease: "easeOut" }}
            >
              <Eyebrow withDot accent="15 90% 58%" className="text-[#9a7258]">
                {pickByLanguage(language, {
                  pt: "Identidade · Audiência · Receita",
                  en: "Identity · Audience · Revenue",
                })}
              </Eyebrow>
            </motion.div>

            <motion.h1
              initial={rm ? false : { opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: rm ? 0 : 0.6, delay: rm ? 0 : 0.08, ease: "easeOut" }}
              className="mt-5 sm:mt-6 font-editorial font-semibold text-[clamp(2.5rem,8vw,5.6rem)] leading-[1] sm:leading-[0.98] tracking-[-0.035em] text-[#201814]"
            >
              {resolvedTitleNode}
            </motion.h1>

            <motion.p
              initial={rm ? false : { opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: rm ? 0 : 0.55, delay: rm ? 0 : 0.16, ease: "easeOut" }}
              className="mt-5 max-w-xl text-[15px] leading-7 text-[#65584e] sm:mt-6 sm:text-lg sm:leading-8"
            >
              {resolvedSubtitle}
            </motion.p>

            <motion.div
              initial={rm ? false : { opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: rm ? 0 : 0.5, delay: rm ? 0 : 0.24, ease: "easeOut" }}
              className="mt-8 flex w-full flex-col items-stretch gap-3 sm:mt-10 sm:w-auto sm:flex-row sm:items-center sm:gap-4"
            >
              <Button
                variant="hero"
                size="lg"
                className="h-14 w-full rounded-full px-8 text-base shadow-[0_18px_40px_rgba(240,127,69,0.28)] transition-transform duration-150 ease-out hover:brightness-95 active:scale-[0.97] sm:w-auto"
                onClick={() => {
                  trackCTAClick(resolvedPrimaryCta, "hero_section", signupPath);
                  navigate(signupPath);
                }}
              >
                {resolvedPrimaryCta}
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="lg"
                asChild
                className="h-14 w-full rounded-full px-6 text-[#3d2f25] hover:bg-[#fff1e1]/80 hover:text-[#201814] sm:w-auto"
              >
                <a
                  href="#story"
                  onClick={() => trackCTAClick(
                    pickByLanguage(language, { pt: "Veja em ação", en: "See it in action" }),
                    "hero_section",
                    "#story",
                  )}
                >
                  {pickByLanguage(language, { pt: "Veja em ação", en: "See it in action" })}
                </a>
              </Button>
            </motion.div>

            {/* Trust strip — microcopy + rating + creator count */}
            <motion.div
              initial={rm ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: rm ? 0 : 0.5, delay: rm ? 0 : 0.32, ease: "easeOut" }}
              className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-[12.5px] text-[#76675b] sm:mt-7"
            >
              <span className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[#2e7d50]" aria-hidden />
                <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#5d4838]">
                  {pickByLanguage(language, {
                    pt: "Grátis · sem cartão",
                    en: "Free · no card required",
                  })}
                </span>
              </span>
              <span className="flex items-center gap-1.5" aria-label="Average rating 4.9">
                <span className="flex items-center gap-0.5 text-[#ef7b43]">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-current" aria-hidden />
                  ))}
                </span>
                <span className="font-mono text-[11px] font-semibold tabular-nums text-[#3d2f25]">
                  4.9
                </span>
                <span className="text-[#a08b7c]">
                  {pickByLanguage(language, { pt: "média", en: "rating" })}
                </span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="font-mono text-[11px] font-semibold tabular-nums text-[#3d2f25]">
                  2.4k+
                </span>
                <span className="text-[#76675b]">
                  {pickByLanguage(language, {
                    pt: "creators construindo aqui",
                    en: "creators building here",
                  })}
                </span>
              </span>
            </motion.div>

            {/* Creator categories — aspiration signal */}
            <motion.div
              initial={rm ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: rm ? 0 : 0.5, delay: rm ? 0 : 0.36, ease: "easeOut" }}
              className="mt-5 flex flex-wrap items-center gap-2 sm:mt-6"
              aria-label={pickByLanguage(language, { pt: "Para quem", en: "Built for" })}
            >
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[#a08b7c]">
                {pickByLanguage(language, { pt: "Feito para", en: "Built for" })}
              </span>
              {pickByLanguage(language, {
                pt: ["Creators", "Influenciadores", "Consultores", "Coaches", "Artistas"],
                en: ["Creators", "Influencers", "Consultants", "Coaches", "Artists"],
              }).map((label) => (
                <span
                  key={label}
                  className="rounded-full border border-[#eadbcc] bg-white/70 px-3 py-1 text-[11.5px] font-medium text-[#5d4838] backdrop-blur-sm"
                >
                  {label}
                </span>
              ))}
            </motion.div>

          </div>

          {/* Modelo — visível apenas em desktop */}
          <motion.div
            initial={rm ? false : { opacity: 0, x: 32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: rm ? 0 : 0.7, delay: rm ? 0 : 0.18, ease: "easeOut" }}
            className="hidden lg:flex lg:justify-end lg:items-end lg:self-end lg:mt-0"
          >
            <img
              src={heroModel}
              alt=""
              aria-hidden="true"
              className="max-h-[80svh] w-auto object-bottom object-contain select-none pointer-events-none"
              draggable={false}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
